'use client';

import { useEffect, useRef } from 'react';

export interface ShaderCanvasProps {
  vertexShader: string;
  fragmentShader: string;
  uniforms?: Record<string, any>;
  onBeforeRender?: (gl: WebGL2RenderingContext) => void;
  onMouseMove?: (e: MouseEvent) => void;
  onMouseDown?: (e: MouseEvent) => void;
  onMouseUp?: (e: MouseEvent) => void;
}

export default function ShaderCanvas({
  vertexShader,
  fragmentShader,
  uniforms = {},
  onBeforeRender,
  onMouseMove,
  onMouseDown,
  onMouseUp,
}: ShaderCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGL2RenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const frameRef = useRef<number>(0);
  const startTimeRef = useRef<number>(Date.now());

  // Initialize WebGL context and program
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Get WebGL2 context
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) {
      console.error('WebGL2 not supported');
      return;
    }
    glRef.current = gl;

    // Enable floating point textures
    const ext = gl.getExtension('EXT_color_buffer_float');
    if (!ext) {
      console.error('Floating point textures not supported');
      return;
    }

    // Create shaders
    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(vs, vertexShader);
    gl.shaderSource(fs, fragmentShader);
    gl.compileShader(vs);
    gl.compileShader(fs);

    // Check vertex shader compilation
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
      console.error('Vertex shader compilation error:', gl.getShaderInfoLog(vs));
      return;
    }

    // Check fragment shader compilation
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      console.error('Fragment shader compilation error:', gl.getShaderInfoLog(fs));
      return;
    }

    // Create program
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    // Check program linking
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program linking error:', gl.getProgramInfoLog(program));
      return;
    }

    programRef.current = program;

    // Create vertex buffer
    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    // Get attribute location
    const positionLocation = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Clean up on unmount
    return () => {
      cancelAnimationFrame(frameRef.current);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
    };
  }, [vertexShader, fragmentShader]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const gl = glRef.current;
      if (!canvas || !gl) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mouse events
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMove = (e: MouseEvent) => onMouseMove?.(e);
    const handleDown = (e: MouseEvent) => onMouseDown?.(e);
    const handleUp = (e: MouseEvent) => onMouseUp?.(e);

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mousedown', handleDown);
    canvas.addEventListener('mouseup', handleUp);

    return () => {
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mousedown', handleDown);
      canvas.removeEventListener('mouseup', handleUp);
    };
  }, [onMouseMove, onMouseDown, onMouseUp]);

  // Animation loop
  useEffect(() => {
    const gl = glRef.current;
    const program = programRef.current;
    if (!gl || !program) return;

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);

      // Call before render callback
      onBeforeRender?.(gl);

      // Use program
      gl.useProgram(program);

      // Clear the canvas
      gl.clearColor(0.98, 0.98, 1.0, 1.0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Set uniforms
      const time = (Date.now() - startTimeRef.current) / 1000;
      const timeLocation = gl.getUniformLocation(program, 'time');
      const resolutionLocation = gl.getUniformLocation(program, 'resolution');

      if (timeLocation !== null) {
        gl.uniform1f(timeLocation, time);
      }

      if (resolutionLocation !== null) {
        gl.uniform2f(resolutionLocation, gl.canvas.width, gl.canvas.height);
      }

      // Set custom uniforms
      Object.entries(uniforms).forEach(([name, value]) => {
        const location = gl.getUniformLocation(program, name);
        if (location === null) return;

        if (Array.isArray(value)) {
          const method = `uniform${value.length}fv` as keyof WebGL2RenderingContext;
          const uniformFunction = gl[method] as (
            location: WebGLUniformLocation,
            data: Float32List
          ) => void;
          uniformFunction(location, value);
        } else if (typeof value === 'number') {
          gl.uniform1f(location, value);
        } else if (value instanceof WebGLTexture) {
          gl.uniform1i(location, 0);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, value);
        }
      });

      // Draw
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    animate();
    return () => cancelAnimationFrame(frameRef.current);
  }, [uniforms, onBeforeRender]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0, // never change this!
      }}
    />
  );
}
